import DataTable, { type TableColumn } from "react-data-table-component";
import { useMemo } from "react";
import { KeyRound, Shield } from "lucide-react";
import { Typography } from "../atoms/Typography";
import { dataTableCustomStyles } from "../../config/dataTableTheme";
import { createDataTablePagination } from "./DataTablePagination";
import type { ManagedUserRow } from "../../types/userManagement";
import { formatDisplayDateTime } from "../../utils/displayDate";
import { Button } from "../atoms/Button";

const usersTablePagination = createDataTablePagination({
  rowsPerPageOptions: [10, 25, 50]
});

const EMPTY_CELL = "—";

/** Admin role codes are stored with underscores; show spaces for readability. */
const formatAdminRoleForDisplay = (code: string): string => code.replaceAll("_", " ").trim();

const formatBusinessUnitsCell = (units: readonly string[]): string => {
  const cleaned = units.map((u) => u.trim()).filter(Boolean);
  if (cleaned.length === 0) return EMPTY_CELL;
  return cleaned.join(", ");
};

const formatGroupsCell = (groups: readonly string[]): string => {
  const cleaned = groups.map((g) => g.trim()).filter(Boolean);
  if (cleaned.length === 0) return EMPTY_CELL;
  return cleaned.join(", ");
};

const formatAdminRolesCell = (roles: readonly string[]): string => {
  const cleaned = roles.map((r) => r.trim()).filter(Boolean);
  if (cleaned.length === 0) return EMPTY_CELL;
  return cleaned.map(formatAdminRoleForDisplay).join(", ");
};

const formatLastLogin = (iso: string | null): string => {
  if (!iso) return "—";
  try {
    return formatDisplayDateTime(iso);
  } catch {
    return "—";
  }
};

type UsersTableProps = {
  readonly users: readonly ManagedUserRow[];
  readonly totalRows: number;
  readonly isLoading: boolean;
  readonly page: number;
  readonly perPage: number;
  readonly onChangePage: (page: number) => void;
  readonly onChangeRowsPerPage: (perPage: number, page: number) => void;
  /** When true, show Edit roles action (caller should only pass true for SUPER_ADMIN). */
  readonly canEditRoles?: boolean;
  readonly onEditRoles?: (row: ManagedUserRow) => void;
};

export const UsersTable = ({
  users,
  totalRows,
  isLoading,
  page,
  perPage,
  onChangePage,
  onChangeRowsPerPage,
  canEditRoles = false,
  onEditRoles
}: UsersTableProps) => {
  const columns: TableColumn<ManagedUserRow>[] = useMemo(() => {
    const base: TableColumn<ManagedUserRow>[] = [
      {
        id: "user_name",
        name: "User Name",
        selector: (row) => row.userName,
        sortable: false,
        grow: 1.5,
        cell: (row) => <Typography variant="body">{row.userName}</Typography>
      },
      {
        id: "email",
        name: "Email ID",
        selector: (row) => row.email,
        sortable: false,
        grow: 2,
        cell: (row) => (
          <Typography variant="body" className="text-primary-600">
            {row.email}
          </Typography>
        )
      },
      {
        id: "last_login",
        name: "Last login",
        sortable: false,
        minWidth: "160px",
        cell: (row) => (
          <Typography variant="body" className="text-gray-700">
            {formatLastLogin(row.lastLoginAt)}
          </Typography>
        )
      },
      {
        id: "business_units",
        name: "Business Units",
        sortable: false,
        minWidth: "200px",
        cell: (row) => (
          <Typography variant="muted" className="text-sm">
            {formatBusinessUnitsCell(row.businessUnits)}
          </Typography>
        )
      },
      {
        id: "groups",
        name: "Groups",
        sortable: false,
        minWidth: "220px",
        grow: 1.5,
        cell: (row) => (
          <Typography variant="muted" className="text-sm">
            {formatGroupsCell(row.groups)}
          </Typography>
        )
      },
      {
        id: "role",
        name: "Role",
        sortable: false,
        grow: 1.2,
        cell: (row) => (
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
            <Typography variant="muted" className="text-sm">
              {formatAdminRolesCell(row.adminRoles)}
            </Typography>
          </div>
        )
      }
    ];
    if (canEditRoles && onEditRoles) {
      base.push({
        id: "edit_roles",
        name: "",
        width: "130px",
        right: true,
        cell: (row) => (
          <Button
            variant="ghost"
            type="button"
            className="!px-2"
            onClick={() => onEditRoles(row)}
            aria-label={`Edit admin roles for ${row.userName}`}
          >
            <Shield className="mr-1.5 h-4 w-4" aria-hidden />
            Roles
          </Button>
        ),
        ignoreRowClick: true
      });
    }
    return base;
  }, [canEditRoles, onEditRoles]);

  return (
    <DataTable
      columns={columns}
      data={[...users]}
      customStyles={dataTableCustomStyles}
      striped={false}
      progressPending={isLoading}
      pagination
      paginationServer
      paginationTotalRows={totalRows}
      paginationPerPage={perPage}
      paginationDefaultPage={page}
      paginationRowsPerPageOptions={[10, 25, 50]}
      paginationComponent={usersTablePagination}
      onChangePage={onChangePage}
      onChangeRowsPerPage={onChangeRowsPerPage}
      keyField="userId"
      noDataComponent={
        <Typography variant="muted" className="py-8 text-center">
          No users match your search.
        </Typography>
      }
    />
  );
};
